/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * BuilderToolbar — top bar: page name, undo/redo, preview, save draft, publish.
 */

import React, { useState, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';

export default function BuilderToolbar({
  pageName,
  pageStatus,
  isDirty,
  canUndo,
  canRedo,
  isSaving,
  isPublishing,
  onUndo,
  onRedo,
  onSaveDraft,
  onPublish,
  onPreview,
  onSeo,
  onHeaderFooter,
  onSavePageTemplate,
  onBack,
}) {
  const { theme } = useTheme();
  const [showPageTpl, setShowPageTpl]   = useState(false);
  const [pageTplName, setPageTplName]   = useState('');
  const [savingTpl,   setSavingTpl]     = useState(false);
  const tplInputRef = useRef(null);

  const openPageTpl = () => {
    setPageTplName('');
    setShowPageTpl(true);
    setTimeout(() => tplInputRef.current?.focus(), 50);
  };

  const handleSavePageTpl = async () => {
    if (!pageTplName.trim() || savingTpl) return;
    setSavingTpl(true);
    try {
      await onSavePageTemplate(pageTplName.trim());
      setShowPageTpl(false);
      setPageTplName('');
    } catch (_) {
      // toast already shown by parent
    } finally {
      setSavingTpl(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        background: '#fff',
        borderBottom: '1px solid #e2e8f0',
        flexShrink: 0,
      }}
    >
      {/* Back */}
      <button
        type="button"
        className="btn btn-sm btn-light"
        onClick={onBack}
        style={{ fontSize: 12 }}
      >
        <i className="bi bi-arrow-left me-1" />
        Back
      </button>

      {/* Page name + status */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pageName || 'Untitled Page'}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 100,
            background: pageStatus === 'published' ? '#dcfce7' : '#fef9c3',
            color: pageStatus === 'published' ? '#15803d' : '#a16207',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            flexShrink: 0,
          }}
        >
          {pageStatus === 'published' ? 'Published' : 'Draft'}
        </span>
        {isDirty && (
          <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>
            (unsaved)
          </span>
        )}
      </div>

      {/* Undo / Redo */}
      <button
        type="button"
        className="btn btn-sm btn-light"
        onClick={onUndo}
        disabled={!canUndo}
        title="Undo (Ctrl+Z)"
        style={{ padding: '4px 8px' }}
      >
        <i className="bi bi-arrow-counterclockwise" />
      </button>
      <button
        type="button"
        className="btn btn-sm btn-light"
        onClick={onRedo}
        disabled={!canRedo}
        title="Redo (Ctrl+Y)"
        style={{ padding: '4px 8px' }}
      >
        <i className="bi bi-arrow-clockwise" />
      </button>

      {/* Header & Footer */}
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={onHeaderFooter}
        style={{ fontSize: 12 }}
        title="Edit header and footer"
      >
        <i className="bi bi-columns-gap me-1" />
        Header &amp; Footer
      </button>

      {/* SEO */}
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={onSeo}
        style={{ fontSize: 12 }}
        title="Edit SEO settings"
      >
        <i className="bi bi-search me-1" />
        SEO
      </button>

      {/* Save Page as Template */}
      {showPageTpl ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap', fontWeight: 600 }}>
            <i className="bi bi-file-earmark-plus me-1" />
            Page Template:
          </span>
          <input
            ref={tplInputRef}
            className="form-control form-control-sm"
            placeholder="Template name…"
            value={pageTplName}
            onChange={e => setPageTplName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter')  handleSavePageTpl();
              if (e.key === 'Escape') { setShowPageTpl(false); setPageTplName(''); }
            }}
            style={{ width: 160, fontSize: 12 }}
          />
          <button
            type="button"
            className="btn btn-sm"
            style={{ background: theme.primary, color: '#fff', border: 'none', fontSize: 12, whiteSpace: 'nowrap' }}
            onClick={handleSavePageTpl}
            disabled={savingTpl || !pageTplName.trim()}
          >
            {savingTpl ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: 12 }}
            onClick={() => { setShowPageTpl(false); setPageTplName(''); }}
          >
            ✕
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={openPageTpl}
          style={{ fontSize: 12 }}
          title="Save the entire page layout as a reusable template"
        >
          <i className="bi bi-file-earmark-plus me-1" />
          Page Template
        </button>
      )}

      {/* Preview */}
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={onPreview}
        style={{ fontSize: 12 }}
      >
        <i className="bi bi-eye me-1" />
        Preview
      </button>

      {/* Save Draft */}
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={onSaveDraft}
        disabled={isSaving || !isDirty}
        style={{ fontSize: 12 }}
      >
        {isSaving ? (
          <>
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
            Saving…
          </>
        ) : (
          <>
            <i className="bi bi-floppy me-1" />
            Save Draft
          </>
        )}
      </button>

      {/* Publish */}
      <button
        type="button"
        className="btn btn-sm"
        onClick={onPublish}
        disabled={isPublishing}
        style={{ background: theme.primary, color: '#fff', border: 'none', fontSize: 12 }}
      >
        {isPublishing ? (
          <>
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
            Publishing…
          </>
        ) : (
          <>
            <i className="bi bi-rocket-takeoff me-1" />
            Publish
          </>
        )}
      </button>
    </div>
  );
}
